import React, { useState, useEffect } from 'react';
import { Edit, Save, X, Plus, Trash2, User, Briefcase, MapPin, Calendar, DollarSign, CreditCard } from 'lucide-react';
import supabase from '../../supabase';
import { useToast } from '../Invoice/Toast';
import './EmployeeInfo.css';

const EmployeeInfo = ({ employee, onEmployeeUpdate, onRefresh }) => {
  const [editMode, setEditMode] = useState({});
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [newFieldInputs, setNewFieldInputs] = useState({
    earnings: { name: '', value: '', showInput: false },
    deductions: { name: '', value: '', showInput: false },
    custom_fields: { name: '', value: '', showInput: false }
  });

  // Initialize toast hook
  const { showError, showSuccess, showWarning, showInfo, ToastContainer } = useToast();

  useEffect(() => {
    if (employee) {
      setOriginalData(employee);
      setEditData({
        ...employee,
        earnings: employee.earnings || {},
        deductions: employee.deductions || {},
        custom_fields: employee.custom_fields || {}
      });
    }
  }, [employee]);

  const handleEditToggle = (section) => {
    if (editMode[section]) {
      // Cancel edit - restore original data
      setEditData(prev => ({
        ...prev,
        ...originalData
      }));
      // Hide any new field inputs
      setNewFieldInputs(prev => ({
        ...prev,
        [section]: { name: '', value: '', showInput: false }
      }));
    }
    setEditMode(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleJsonFieldChange = (section, key, value, isDelete = false) => {
    setEditData(prev => {
      const newSection = { ...prev[section] };
      if (isDelete) {
        delete newSection[key];
      } else {
        newSection[key] = section === 'custom_fields' ? value : parseFloat(value) || 0;
      }
      return {
        ...prev,
        [section]: newSection
      };
    });
  };

  const handleNewFieldInputChange = (section, field, value) => {
    setNewFieldInputs(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const showNewFieldInput = (section) => {
    setNewFieldInputs(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        showInput: true
      }
    }));
  };

  const cancelNewField = (section) => {
    setNewFieldInputs(prev => ({
      ...prev,
      [section]: { name: '', value: '', showInput: false }
    }));
  };

  const addNewField = (section) => {
    const { name, value } = newFieldInputs[section];
    
    if (!name.trim()) {
      showError('Validation Error', 'Please enter a field name');
      return;
    }

    if (editData[section] && editData[section][name.trim()]) {
      showError('Duplicate Field', 'Field name already exists');
      return;
    }

    const processedValue = section === 'custom_fields' 
      ? value.trim() || ''
      : parseFloat(value) || 0;

    setEditData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [name.trim()]: processedValue
      }
    }));

    // Reset the new field inputs
    setNewFieldInputs(prev => ({
      ...prev,
      [section]: { name: '', value: '', showInput: false }
    }));

    showSuccess('Field Added', `${name.trim()} added successfully`);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        showError('File Too Large', 'Image size should be less than 5MB');
        e.target.value = '';
        return;
      }

      // Validate file type
      if (!file.type.startsWith('image/')) {
        showError('Invalid File Type', 'Please select a valid image file');
        e.target.value = '';
        return;
      }

      setEditData(prev => ({
        ...prev,
        imageFile: file
      }));
      showInfo('Image Selected', 'Image will be uploaded when you save changes');
    }
  };

  const uploadImage = async (file) => {
    if (!file) return null;

    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${editData.emp_id}-${Date.now()}.${fileExt}`;
      const filePath = `employee-images/${fileName}`;

      const { data, error } = await supabase.storage
        .from('employee-images')
        .upload(filePath, file);

      if (error) throw error;

      const { data: publicData } = supabase.storage
        .from('employee-images')
        .getPublicUrl(filePath);

      return publicData.publicUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      showError('Upload Error', 'Failed to upload profile image');
      return null;
    }
  };

  const validatePersonalInfo = () => {
    if (!editData.name || !editData.name.trim()) {
      showError('Validation Error', 'Employee name is required');
      return false;
    }

    if (!editData.emp_id || !editData.emp_id.trim()) {
      showError('Validation Error', 'Employee ID is required');
      return false;
    }

    if (!editData.emp_role || !editData.emp_role.trim()) {
      showError('Validation Error', 'Employee role is required');
      return false;
    }

    if (!editData.salary || parseFloat(editData.salary) <= 0) {
      showError('Validation Error', 'Please enter a valid salary amount');
      return false;
    }

    if (!editData.joining_date) {
      showError('Validation Error', 'Joining date is required');
      return false;
    }

    if (!editData.location || !editData.location.trim()) {
      showError('Validation Error', 'Location is required');
      return false;
    }

    return true;
  };

  const validateBankingInfo = () => {
    if (!editData.pan_card || !editData.pan_card.trim()) {
      showError('Validation Error', 'PAN card number is required');
      return false;
    }

    // PAN card format validation
    const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
    if (!panRegex.test(editData.pan_card.toUpperCase())) {
      showError('Validation Error', 'Invalid PAN card format (e.g., AAAAA0000A)');
      return false;
    }

    if (!editData.emp_bank_name || !editData.emp_bank_name.trim()) {
      showError('Validation Error', 'Bank name is required');
      return false;
    }

    if (!editData.account_no || !editData.account_no.trim()) {
      showError('Validation Error', 'Account number is required');
      return false;
    }

    if (!editData.ifsc_code || !editData.ifsc_code.trim()) {
      showError('Validation Error', 'IFSC code is required');
      return false;
    }

    // IFSC code format validation
    const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
    if (!ifscRegex.test(editData.ifsc_code.toUpperCase())) {
      showError('Validation Error', 'Invalid IFSC code format (e.g., SBIN0000123)');
      return false;
    }

    if (!editData.branch_name || !editData.branch_name.trim()) {
      showError('Validation Error', 'Branch name is required');
      return false;
    }

    return true;
  };

  const handleSave = async (section) => {
    // Validate based on section
    if (section === 'personal' && !validatePersonalInfo()) {
      return;
    }

    if (section === 'banking' && !validateBankingInfo()) {
      return;
    }

    setLoading(true);
    try {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        showError('Authentication Error', 'User not authenticated');
        setLoading(false);
        return;
      }

      let updateData = {};
      let changedFields = [];

      // Handle image upload if needed
      if (editData.imageFile) {
        showInfo('Uploading', 'Uploading profile image...');
        const imageUrl = await uploadImage(editData.imageFile);
        if (imageUrl) {
          updateData.image = imageUrl;
          changedFields.push('Profile Image');
        } else {
          setLoading(false);
          return;
        }
      }

      switch (section) {
        case 'personal':
          const personalFields = ['name', 'emp_role', 'emp_id', 'joining_date', 'location', 'salary'];
          personalFields.forEach(field => {
            if (editData[field] !== originalData[field]) {
              updateData[field] = field === 'salary' ? parseFloat(editData[field]) : editData[field];
              changedFields.push(field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
            }
          });
          break;

        case 'banking':
          const bankingFields = ['emp_bank_name', 'account_no', 'ifsc_code', 'pan_card', 'branch_name', 'pf_no'];
          bankingFields.forEach(field => {
            if (editData[field] !== originalData[field]) {
              updateData[field] = field === 'pan_card' || field === 'ifsc_code' 
                ? editData[field].toUpperCase() 
                : editData[field];
              changedFields.push(field.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()));
            }
          });
          break;

        case 'earnings':
          if (JSON.stringify(editData.earnings) !== JSON.stringify(originalData.earnings)) {
            updateData.earnings = editData.earnings;
            changedFields.push('Earnings');
          }
          break;

        case 'deductions':
          if (JSON.stringify(editData.deductions) !== JSON.stringify(originalData.deductions)) {
            updateData.deductions = editData.deductions;
            changedFields.push('Deductions');
          }
          break;

        case 'custom':
          if (JSON.stringify(editData.custom_fields) !== JSON.stringify(originalData.custom_fields)) {
            updateData.custom_fields = editData.custom_fields;
            changedFields.push('Custom Fields');
          }
          break;
      }

      if (Object.keys(updateData).length > 0) {
        const { data, error } = await supabase
          .from('employees')
          .update(updateData)
          .eq('id', employee.id)
          .eq('user_id', user.id)
          .select()
          .single();

        if (error) throw error;

        // Update local state
        const updatedEmployee = { ...employee, ...updateData };
        setOriginalData(updatedEmployee);
        setEditData(prev => ({ ...prev, ...updateData, imageFile: null }));
        onEmployeeUpdate(updatedEmployee);

        showSuccess('Update Successful', `Successfully updated: ${changedFields.join(', ')}`);
      } else {
        showInfo('No Changes', 'No changes detected to save');
      }

      setEditMode(prev => ({ ...prev, [section]: false }));
    } catch (error) {
      console.error('Error updating employee:', error);
      showError('Update Error', error.message || 'Failed to update employee information');
    } finally {
      setLoading(false);
    }
  };

  const generateFallbackAvatar = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'Unknown')}&size=120&background=000000&color=ffffff&bold=true`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const renderJsonFieldsSection = (section, sectionData) => {
    const isCustomFields = section === 'custom_fields';
    const fieldInputType = isCustomFields ? 'text' : 'number';
    const fieldInputStep = isCustomFields ? undefined : '0.01';

    return (
      <div className="empinfo-json-fields">
        {Object.keys(sectionData || {}).length === 0 && !newFieldInputs[section].showInput ? (
          <p className="empinfo-empty-state">No {section.replace('_', ' ')} data available</p>
        ) : (
          <>
            {Object.entries(sectionData || {}).map(([key, value]) => (
              <div key={key} className="empinfo-json-field">
                <div className="empinfo-json-field-content">
                  <span className="empinfo-json-key">{key}</span>
                  {editMode[section] ? (
                    <div className="empinfo-json-edit-row">
                      <input
                        type={fieldInputType}
                        value={value}
                        onChange={(e) => handleJsonFieldChange(section, key, e.target.value)}
                        className="empinfo-json-input"
                        step={fieldInputStep}
                      />
                      <button
                        onClick={() => handleJsonFieldChange(section, key, null, true)}
                        className="empinfo-delete-btn"
                        title="Delete field"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ) : (
                    <span className="empinfo-json-value">
                      {isCustomFields ? (value || 'N/A') : formatCurrency(value)}
                    </span>
                  )}
                </div>
              </div>
            ))}

            {/* New Field Input */}
            {editMode[section] && newFieldInputs[section].showInput && (
              <div className="empinfo-json-field empinfo-new-field">
                <div className="empinfo-new-field-content">
                  <div className="empinfo-new-field-inputs">
                    <input
                      type="text"
                      placeholder="Field name"
                      value={newFieldInputs[section].name}
                      onChange={(e) => handleNewFieldInputChange(section, 'name', e.target.value)}
                      className="empinfo-new-field-name-input"
                    />
                    <input
                      type={fieldInputType}
                      placeholder={isCustomFields ? "Field value" : "Amount"}
                      value={newFieldInputs[section].value}
                      onChange={(e) => handleNewFieldInputChange(section, 'value', e.target.value)}
                      className="empinfo-new-field-value-input"
                      step={fieldInputStep}
                    />
                  </div>
                  <div className="empinfo-new-field-actions">
                    <button
                      onClick={() => addNewField(section)}
                      className="empinfo-confirm-add-btn"
                      title="Add field"
                    >
                      <Save size={14} />
                    </button>
                    <button
                      onClick={() => cancelNewField(section)}
                      className="empinfo-cancel-add-btn"
                      title="Cancel"
                    >
                      <X size={14} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Add Field Button */}
            {editMode[section] && !newFieldInputs[section].showInput && (
              <button
                onClick={() => showNewFieldInput(section)}
                className="empinfo-add-field-btn"
              >
                <Plus size={16} />
                Add {isCustomFields ? 'Custom Field' : (section === 'earnings' ? 'Earning' : 'Deduction')}
              </button>
            )}
          </>
        )}
      </div>
    );
  };

  if (!employee) return null;

  return (
    <>
      {/* Toast Container */}
      <ToastContainer />

      <div className="empinfo-container">
        {/* Profile Section */}
        <div className="empinfo-section">
          <div className="empinfo-section-header">
            <div className="empinfo-section-title">
              <User className="empinfo-section-icon" />
              <h2>Profile Information</h2>
            </div>
            <button
              className={`empinfo-edit-btn ${editMode.personal ? 'empinfo-edit-btn-active' : ''}`}
              onClick={() => handleEditToggle('personal')}
              disabled={loading}
            >
              {editMode.personal ? <X size={16} /> : <Edit size={16} />}
              {editMode.personal ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="empinfo-profile-content">
            <div className="empinfo-profile-image-section">
              <img
                src={editData.image || generateFallbackAvatar(editData.name)}
                alt={`${editData.name}'s profile`}
                className="empinfo-profile-image"
                onError={(e) => { e.target.src = generateFallbackAvatar(editData.name); }}
              />
              {editMode.personal && (
                <div className="empinfo-image-upload">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="empinfo-file-input"
                    id="profile-image"
                  />
                  <label htmlFor="profile-image" className="empinfo-file-label">
                    Change Photo
                  </label>
                </div>
              )}
            </div>

            <div className="empinfo-profile-details">
              <div className="empinfo-field-group">
                <div className="empinfo-field">
                  <label className="empinfo-field-label">Employee Name</label>
                  {editMode.personal ? (
                    <input
                      type="text"
                      value={editData.name || ''}
                      onChange={(e) => handleInputChange('name', e.target.value)}
                      className="empinfo-field-input"
                    />
                  ) : (
                    <p className="empinfo-field-value">{editData.name || 'N/A'}</p>
                  )}
                </div>

                <div className="empinfo-field">
                  <label className="empinfo-field-label">Employee ID</label>
                  {editMode.personal ? (
                    <input
                      type="text"
                      value={editData.emp_id || ''}
                      onChange={(e) => handleInputChange('emp_id', e.target.value)}
                      className="empinfo-field-input"
                    />
                  ) : (
                    <p className="empinfo-field-value">{editData.emp_id || 'N/A'}</p>
                  )}
                </div>

                <div className="empinfo-field">
                  <label className="empinfo-field-label">Role</label>
                  {editMode.personal ? (
                    <input
                      type="text"
                      value={editData.emp_role || ''}
                      onChange={(e) => handleInputChange('emp_role', e.target.value)}
                      className="empinfo-field-input"
                    />
                  ) : (
                    <p className="empinfo-field-value">{editData.emp_role || 'N/A'}</p>
                  )}
                </div>

                <div className="empinfo-field">
                  <label className="empinfo-field-label">CTC</label>
                  {editMode.personal ? (
                    <input
                      type="number"
                      value={editData.salary || ''}
                      onChange={(e) => handleInputChange('salary', e.target.value)}
                      className="empinfo-field-input"
                      step="0.01"
                    />
                  ) : (
                    <p className="empinfo-field-value">{formatCurrency(editData.salary)}</p>
                  )}
                </div>

                <div className="empinfo-field">
                  <label className="empinfo-field-label">Joining Date</label>
                  {editMode.personal ? (
                    <input
                      type="date"
                      value={editData.joining_date || ''}
                      onChange={(e) => handleInputChange('joining_date', e.target.value)}
                      className="empinfo-field-input"
                    />
                  ) : (
                    <p className="empinfo-field-value">{formatDate(editData.joining_date)}</p>
                  )}
                </div>

                <div className="empinfo-field">
                  <label className="empinfo-field-label">Location</label>
                  {editMode.personal ? (
                    <input
                      type="text"
                      value={editData.location || ''}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="empinfo-field-input"
                    />
                  ) : (
                    <p className="empinfo-field-value">{editData.location || 'N/A'}</p>
                  )}
                </div>
              </div>

              {editMode.personal && (
                <div className="empinfo-section-actions">
                  <button
                    onClick={() => handleSave('personal')}
                    disabled={loading}
                    className="empinfo-save-btn"
                  >
                    <Save size={16} />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Banking Information */}
        <div className="empinfo-section">
          <div className="empinfo-section-header">
            <div className="empinfo-section-title">
              <CreditCard className="empinfo-section-icon" />
              <h2>Banking Information</h2>
            </div>
            <button
              className={`empinfo-edit-btn ${editMode.banking ? 'empinfo-edit-btn-active' : ''}`}
              onClick={() => handleEditToggle('banking')}
              disabled={loading}
            >
              {editMode.banking ? <X size={16} /> : <Edit size={16} />}
              {editMode.banking ? 'Cancel' : 'Edit'}
            </button>
          </div>

          <div className="empinfo-field-grid">
            <div className="empinfo-field">
              <label className="empinfo-field-label">PAN Card</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.pan_card || ''}
                  onChange={(e) => handleInputChange('pan_card', e.target.value)}
                  className="empinfo-field-input"
                  placeholder="AAAAA0000A"
                  maxLength="10"
                />
              ) : (
                <p className="empinfo-field-value">{editData.pan_card || 'N/A'}</p>
              )}
            </div>

            <div className="empinfo-field">
              <label className="empinfo-field-label">Bank Name</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.emp_bank_name || ''}
                  onChange={(e) => handleInputChange('emp_bank_name', e.target.value)}
                  className="empinfo-field-input"
                />
              ) : (
                <p className="empinfo-field-value">{editData.emp_bank_name || 'N/A'}</p>
              )}
            </div>

            <div className="empinfo-field">
              <label className="empinfo-field-label">Account Number</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.account_no || ''}
                  onChange={(e) => handleInputChange('account_no', e.target.value)}
                  className="empinfo-field-input"
                />
              ) : (
                <p className="empinfo-field-value">{editData.account_no || 'N/A'}</p>
              )}
            </div>

            <div className="empinfo-field">
              <label className="empinfo-field-label">IFSC Code</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.ifsc_code || ''}
                  onChange={(e) => handleInputChange('ifsc_code', e.target.value)}
                  className="empinfo-field-input"
                  placeholder="SBIN0000123"
                  maxLength="11"
                />
              ) : (
                <p className="empinfo-field-value">{editData.ifsc_code || 'N/A'}</p>
              )}
            </div>

            <div className="empinfo-field">
              <label className="empinfo-field-label">Branch Name</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.branch_name || ''}
                  onChange={(e) => handleInputChange('branch_name', e.target.value)}
                  className="empinfo-field-input"
                />
              ) : (
                <p className="empinfo-field-value">{editData.branch_name || 'N/A'}</p>
              )}
            </div>

            <div className="empinfo-field">
              <label className="empinfo-field-label">PF Number</label>
              {editMode.banking ? (
                <input
                  type="text"
                  value={editData.pf_no || ''}
                  onChange={(e) => handleInputChange('pf_no', e.target.value)}
                  className="empinfo-field-input"
                />
              ) : (
                <p className="empinfo-field-value">{editData.pf_no || 'N/A'}</p>
              )}
            </div>
          </div>

          {editMode.banking && (
            <div className="empinfo-section-actions">
              <button
                onClick={() => handleSave('banking')}
                disabled={loading}
                className="empinfo-save-btn"
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>

        {/* Earnings and Deductions - Side by Side */}
        <div className="empinfo-earnings-deductions-container">
          {/* Earnings */}
          <div className="empinfo-section empinfo-earnings-section">
            <div className="empinfo-section-header">
              <div className="empinfo-section-title">
                <Plus className="empinfo-section-icon" />
                <h2>Earnings</h2>
              </div>
              <button
                className={`empinfo-edit-btn ${editMode.earnings ? 'empinfo-edit-btn-active' : ''}`}
                onClick={() => handleEditToggle('earnings')}
                disabled={loading}
              >
                {editMode.earnings ? <X size={16} /> : <Edit size={16} />}
                {editMode.earnings ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {renderJsonFieldsSection('earnings', editData.earnings)}

            {editMode.earnings && (
              <div className="empinfo-section-actions">
                <button
                  onClick={() => handleSave('earnings')}
                  disabled={loading}
                  className="empinfo-save-btn"
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>

          {/* Deductions */}
          <div className="empinfo-section empinfo-deductions-section">
            <div className="empinfo-section-header">
              <div className="empinfo-section-title">
                <Trash2 className="empinfo-section-icon" />
                <h2>Deductions</h2>
              </div>
              <button
                className={`empinfo-edit-btn ${editMode.deductions ? 'empinfo-edit-btn-active' : ''}`}
                onClick={() => handleEditToggle('deductions')}
                disabled={loading}
              >
                {editMode.deductions ? <X size={16} /> : <Edit size={16} />}
                {editMode.deductions ? 'Cancel' : 'Edit'}
              </button>
            </div>

            {renderJsonFieldsSection('deductions', editData.deductions)}

            {editMode.deductions && (
              <div className="empinfo-section-actions">
                <button
                  onClick={() => handleSave('deductions')}
                  disabled={loading}
                  className="empinfo-save-btn"
                >
                  <Save size={16} />
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Custom Fields */}
        <div className="empinfo-section">
          <div className="empinfo-section-header">
            <div className="empinfo-section-title">
              <Briefcase className="empinfo-section-icon" />
              <h2>Custom Fields</h2>
            </div>
            <button
              className={`empinfo-edit-btn ${editMode.custom ? 'empinfo-edit-btn-active' : ''}`}
              onClick={() => handleEditToggle('custom')}
              disabled={loading}
            >
              {editMode.custom ? <X size={16} /> : <Edit size={16} />}
              {editMode.custom ? 'Cancel' : 'Edit'}
            </button>
          </div>

          {renderJsonFieldsSection('custom_fields', editData.custom_fields)}

          {editMode.custom && (
            <div className="empinfo-section-actions">
              <button
                onClick={() => handleSave('custom')}
                disabled={loading}
                className="empinfo-save-btn"
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeInfo;